/**
 * @module design/goldenGeometry
 * @description Golden-ratio type, spacing, leading and layout tokens, derived
 * from the universal `GOLDEN_MAJOR` constant and written to :root before the
 * first render. Same tiered model as afj-pledge-addon
 * (~/Dev/_docs/2026-07-18_SHARED_PACKAGES_SOP.md, "Tiered design tokens"):
 *
 * Tier 1 (universal) : GOLDEN_MAJOR                         — package
 * Tier 1 (project)   : BASE_FONT_PX                         — this file, set once
 * Tier 2 (semantic)  : --text-*, --space-*, --leading-*,
 *                      --grid-major-minor, --golden-minor   — derived here
 * Tier 3 (component) : consumed in component CSS via var(--…)
 *
 * Components keep using the semantic names they already use (--text-md,
 * --space-4…); only their VALUES now come from φ. No component CSS changed
 * to adopt the scale.
 *
 * // TEMPORARY: phiStep/rem logic duplicates afj-pledge-addon/src/design/goldenGeometry.ts.
 *   What caused this: the builder lives in the add-on, not in the shared package.
 *   Why we chose it: moving it into @jochantrelle/golden-ratio-splitter is
 *   shared-package work for its own session (orders file), not this demo window.
 *   Side-effects: a change to the φ model must be made in both files until the
 *   package exports a token builder (Open Loop).
 */
import { GOLDEN_MAJOR } from "@jochantrelle/golden-ratio-splitter/constants";

/** φ ≈ 1.618, derived rather than re-typed as a literal. */
export const PHI: number = 1 / GOLDEN_MAJOR;

/** Project primitive: body text size the scale grows from (matches the add-on). */
export const BASE_FONT_PX: number = 17;

/**
 * Scale position → multiplier. One step = √φ, so two steps = φ.
 * Quarter steps (±0.5) give UI text enough rungs while staying powers of φ.
 * @param step 0 = base, 2 = ×φ, -2 = ×1/φ.
 */
export const phiStep = (step: number): number => Math.pow(Math.sqrt(PHI), step);

/** Semantic type scale → φ step. Base 17px: xs 13.4 · sm 15.1 · md 17 · lg 19.2 · xl 21.6 · 2xl 27.5px. */
const TYPE_STEPS: Readonly<Record<string, number>> = {
    "--text-xs": -1, "--text-sm": -0.5, "--text-md": 0, "--text-lg": 0.5, "--text-xl": 1, "--text-2xl": 2,
};

/** Semantic spacing scale → φ step. 6.5 · 10.5 · 13.4 · 17 · 21.6 · 27.5 · 44.5 · 72px. */
const SPACE_STEPS: Readonly<Record<string, number>> = {
    "--space-1": -4, "--space-2": -2, "--space-3": -1, "--space-4": 0,
    "--space-5": 1, "--space-6": 2, "--space-7": 4, "--space-8": 6,
};

/** @returns every derived token as [css variable → value]. Pure (testable). */
export function buildGoldenTokens(): Record<string, string> {
    const rem = (n: number): string => `${n.toFixed(4)}rem`;
    const tokens: Record<string, string> = {
        "--phi": PHI.toFixed(10),
        "--golden-major": `${(GOLDEN_MAJOR * 100).toFixed(4)}%`,
        "--golden-minor": `${((1 - GOLDEN_MAJOR) * 100).toFixed(4)}%`,
        // Grid tracks in fr: percentages plus a gap would overflow the row.
        "--grid-major-minor": `minmax(0, ${PHI.toFixed(6)}fr) minmax(0, 1fr)`,
        "--grid-minor-major": `minmax(0, 1fr) minmax(0, ${PHI.toFixed(6)}fr)`,
        "--base-font": `${BASE_FONT_PX}px`,
        "--leading-body": PHI.toFixed(4),
        // Display lines: √φ (≈1.272) keeps large serif headings compact.
        "--leading-tight": phiStep(1).toFixed(4),
        // Hero/page titles: fluid between step 3 (35px) and step 5 (56.6px).
        "--text-3xl": `clamp(${rem(phiStep(3))}, 1.4rem + 3vw, ${rem(phiStep(5))})`,
    };
    Object.entries(TYPE_STEPS).forEach(([k, s]) => (tokens[k] = rem(phiStep(s))));
    Object.entries(SPACE_STEPS).forEach(([k, s]) => (tokens[k] = rem(phiStep(s))));
    return tokens;
}

/** Writes the golden tokens onto :root. Called once in main.tsx before first render. */
export function applyGoldenTokens(root: HTMLElement = document.documentElement): void {
    Object.entries(buildGoldenTokens()).forEach(([k, v]) => root.style.setProperty(k, v));
}
